import React from "react";
import RoundedRectangle from "../RoundedRectangle";
import ClavaImage from "../ClavaImage";
import type { Image } from "../../../client/api";

export interface GridRectangleItem {
  id: string | number;
  label: string;
  image?: Image | null;
  onClick: () => void;
  isSelected?: boolean;
}

export interface GridRectangleProps {
  items: GridRectangleItem[];
  columns?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
  };
  loading?: boolean;
  loadingText?: string;
  className?: string;
  itemClassName?: string;
  showImages?: boolean;
  imageWidth?: number;
  compact?: boolean;
}

const GridRectangle: React.FC<GridRectangleProps> = ({
                                                       items,
                                                       columns = {
                                                         mobile: 1,
                                                         tablet: 2,
                                                         desktop: 3
                                                       },
                                                       loading = false,
                                                       loadingText = "Loading...",
                                                       className = "",
                                                       itemClassName = "",
                                                       showImages = false,
                                                       imageWidth = 48,
                                                       compact = false
                                                     }) => {
  const getGridClasses = () => {
    const gridClassMap: Record<number, { base: string; desktop: string }> = {
      1: { base: "grid-cols-1", desktop: "xl:grid-cols-1" },
      2: { base: "grid-cols-2", desktop: "xl:grid-cols-2" },
      3: { base: "grid-cols-2", desktop: "xl:grid-cols-3" },
      4: { base: "grid-cols-2", desktop: "xl:grid-cols-4" },
      5: { base: "grid-cols-2", desktop: "xl:grid-cols-5" },
      6: { base: "grid-cols-2", desktop: "xl:grid-cols-6" }
    };

    const baseCols = columns.tablet || columns.mobile || 1;
    const desktopCols = columns.desktop || 3;

    const baseGrid = gridClassMap[baseCols]?.base || gridClassMap[1].base;
    const desktopGrid = gridClassMap[desktopCols]?.desktop || gridClassMap[3].desktop;

    return `grid ${baseGrid} ${desktopGrid} gap-4`;
  };

  if (loading) {
    return (
      <div className="text-font-600 dark:text-font-600-dark text-xs">
        {loadingText}
      </div>
    );
  }

  return (
    <div className={`${compact ? "flex flex-col gap-2 items-stretch w-full" : getGridClasses()} ${className}`}>
      {items.map((item) => (
        <RoundedRectangle
          key={item.id}
          className={`${compact ? "h-10 w-full" : "w-full min-h-20"} bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-50-dark focus:outline-none focus:ring-2 focus:ring-primary ${
            item.isSelected ? "ring-2 ring-primary bg-primary-50 dark:bg-primary-50-dark" : ""
          } ${itemClassName}`}
          hasBorder
          hasTitle={false}
          onClick={item.onClick}
        >
          <div
            className={`flex items-center min-w-0 ${compact ? "gap-2 px-3 justify-start h-full" : "gap-3 p-4 h-full"}`}>
            {showImages && item.image && (
              <div className="flex-shrink-0">
                <ClavaImage image={item.image ?? undefined} width={compact ? imageWidth * 0.75 : imageWidth} />
              </div>
            )}
            <span
              className={`text-font dark:text-font-dark truncate whitespace-nowrap ${compact ? "text-xs font-medium text-left" : "text-sm font-medium"}`}>
                            {item.label}
                        </span>
          </div>
        </RoundedRectangle>
      ))}
    </div>
  );
};

export default GridRectangle;
